/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CreateServiceItemRequest } from '../models/create-service-item-request';
import { GenericResponse } from '../models/generic-response';
import { GetServiceItemResponse } from '../models/get-service-item-response';
import { ServiceItemResponse } from '../models/service-item-response';
import { UpdateServiceItemRequest } from '../models/update-service-item-request';

/**
 * Service item APIs
 */
@Injectable({ providedIn: 'root' })
export class ServiceItemService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllServiceItems()` */
  static readonly GetAllServiceItemsPath = '/api/v1/service-items';

  /**
   * Get service item list.
   *
   * Returns all service items as pages.
   * The results will be filtered, sorted based on the provided queries.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllServiceItems()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllServiceItems$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       *   - name
       *   - admin percentage
       *   - unit
       *     - name
       */
      search?: string;

      /**
       * This enables filtering of service items based on the unit id.
       */
      unit?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetServiceItemResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ServiceItemService.GetAllServiceItemsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('unit', params.unit, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetServiceItemResponse>;
        })
      );
  }

  /**
   * Get service item list.
   *
   * Returns all service items as pages.
   * The results will be filtered, sorted based on the provided queries.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllServiceItems$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllServiceItems(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       *   - name
       *   - admin percentage
       *   - unit
       *     - name
       */
      search?: string;

      /**
       * This enables filtering of service items based on the unit id.
       */
      unit?: number;
    },
    context?: HttpContext
  ): Observable<GetServiceItemResponse> {
    return this.getAllServiceItems$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetServiceItemResponse>
        ): GetServiceItemResponse => r.body
      )
    );
  }

  /** Path part for operation `createServiceItem()` */
  static readonly CreateServiceItemPath = '/api/v1/service-items';

  /**
   * Create new service item.
   *
   * Create a new service item to the backend.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createServiceItem()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createServiceItem$Response(
    params: {
      body: CreateServiceItemRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ServiceItemResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ServiceItemService.CreateServiceItemPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ServiceItemResponse>;
        })
      );
  }

  /**
   * Create new service item.
   *
   * Create a new service item to the backend.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createServiceItem$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createServiceItem(
    params: {
      body: CreateServiceItemRequest;
    },
    context?: HttpContext
  ): Observable<ServiceItemResponse> {
    return this.createServiceItem$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ServiceItemResponse>): ServiceItemResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `getServiceItemById()` */
  static readonly GetServiceItemByIdPath = '/api/v1/service-items/{id}';

  /**
   * fetch service item details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getServiceItemById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getServiceItemById$Response(
    params: {
      /**
       * The service item id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ServiceItemResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ServiceItemService.GetServiceItemByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ServiceItemResponse>;
        })
      );
  }

  /**
   * fetch service item details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getServiceItemById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getServiceItemById(
    params: {
      /**
       * The service item id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<ServiceItemResponse> {
    return this.getServiceItemById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ServiceItemResponse>): ServiceItemResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `deleteServiceItemById()` */
  static readonly DeleteServiceItemByIdPath = '/api/v1/service-items/{id}';

  /**
   * Delete Service item  by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteServiceItemById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteServiceItemById$Response(
    params: {
      /**
       * The service item id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ServiceItemService.DeleteServiceItemByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete Service item  by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteServiceItemById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteServiceItemById(
    params: {
      /**
       * The service item id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteServiceItemById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateServiceItemById()` */
  static readonly UpdateServiceItemByIdPath = '/api/v1/service-items/{id}';

  /**
   * Update service item by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateServiceItemById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateServiceItemById$Response(
    params: {
      /**
       * The service item's id that needs to be updated
       */
      id: number;
      body: UpdateServiceItemRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ServiceItemResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ServiceItemService.UpdateServiceItemByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ServiceItemResponse>;
        })
      );
  }

  /**
   * Update service item by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateServiceItemById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateServiceItemById(
    params: {
      /**
       * The service item's id that needs to be updated
       */
      id: number;
      body: UpdateServiceItemRequest;
    },
    context?: HttpContext
  ): Observable<ServiceItemResponse> {
    return this.updateServiceItemById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ServiceItemResponse>): ServiceItemResponse =>
          r.body
      )
    );
  }
}
